import React, { PropsWithChildren, useRef, useState, useCallback } from "react";

import Image from "next/image";
import LiveChatIcon from "public/icons/chat.svg";
import CrossIcon from "public/icons/close.svg";
import MenuIcon from "public/icons/menu.svg";
import MailBoxIcon from "public/icons/opened-mail-icon.svg";

import {
  Collapse,
  useMediaQuery,
  Theme,
  Drawer,
  Checkbox,
} from "@mui/material";

import { useLeftBarData } from "@/api/left-panel/hooks";
import { GetLeftInfoResponseDTOSuccess } from "@/api/left-panel/models";
import { Button } from "@/components/form/Button/Button";
import { ButtonWithIcon } from "@/components/form/Button/ButtonWithIcon";
import { Old_Button } from "@/components/form/Button/Old_Button";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { Paper } from "@/components/paper/Paper";
import { CLOUDFRONT_FONTS_LINK } from "@/constants/links";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { AuthRenderer } from "@/context/auth-renderer/AuthRenderer";
import { CustomScroll } from "@/lib/custom-scroll-component/CustomScroll";
import { isFeatureFlagEnabled } from "@/lib/feature-flags";
import { scrollBarSxStyles } from "@/shared/styles";
import { clsxm } from "@/utils/tailwindMerge";
import { makeUrl } from "@/utils/url-utils/urlMaker";

import { Footer } from "../footer/Footer";
import { LeftMenu } from "./components/left-menu/LeftMenu";
import { SecondaryLayoutTopBanner } from "./components/secondary-layout-top-banner/SecondaryLayoutTopBanner";
import { useIsMainBlockScrolledDown } from "./helpers";

type PropTypes = PropsWithChildren & {
  beforeContent?: JSX.Element;
};

export const SecondaryLayout: React.FC<PropTypes> = ({
  children,
  beforeContent,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const isBelowDesktop = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg"),
  );
  const mainContentRef = useRef<HTMLDivElement>(null);

  const leftMenuRef = useRef<{
    openChatWindow: (type: "liveChat" | "sendRose") => void;
    handleNavigation: (type: string) => void;
  } | null>(null);

  const drawerLeftMenuRef = useRef<{
    openChatWindow: (type: "liveChat" | "sendRose") => void;
    handleNavigation: (type: string) => void;
  } | null>(null);

  const isMainBlockScrolledDown = useIsMainBlockScrolledDown(mainContentRef);

  const { openModal, removeOne } = useOpenModal();
  const openSignInModal = () =>
    openModal("SIGN_IN_FORM_MODAL", { onClose: removeOne });

  const openRegisterModal = () =>
    openModal("REGISTER_FORM_MODAL", { onClose: removeOne });

  const handleClick = () => {
    setMenuIsOpen((value) => !value);
  };

  const { TEMPORARY_toggleNextAuth, TEMPORARY_useNextAuth, isAuthenticated } =
    useAuthContext();

  const handleCheckboxChange = () => {
    TEMPORARY_toggleNextAuth();
    window.location.reload();
  };

  const handleChatButtonClick = useCallback(() => {
    if (menuIsOpen && drawerLeftMenuRef.current) {
      drawerLeftMenuRef.current.openChatWindow("liveChat");
    } else if (leftMenuRef.current) {
      leftMenuRef.current.openChatWindow("liveChat");
    }

    menuIsOpen && handleClick();
  }, [menuIsOpen]);

  const handleMailboxButtonClick = useCallback(() => {
    if (menuIsOpen && drawerLeftMenuRef.current) {
      drawerLeftMenuRef.current.handleNavigation("/mailbox-inbox");
    } else if (leftMenuRef.current) {
      leftMenuRef.current.handleNavigation("/mailbox-inbox");
    }

    menuIsOpen && handleClick();
  }, [menuIsOpen]);

  const { data } = useLeftBarData();
  const parsed = GetLeftInfoResponseDTOSuccess.safeParse(data);

  return (
    <div className="bg-grey1-50">
      {(process.env.SITE_ENV === "tgb" || process.env.SITE_ENV === "local") && (
        <div className="bg-grey1-900 absolute left-1 top-1 bg-opacity-70 px-1 z-[11] h-4 flex gap-1">
          <span className="text-white font-thin text-sm">New client Auth</span>
          <Checkbox
            checked={TEMPORARY_useNextAuth}
            onClick={handleCheckboxChange}
            size="small"
            color="warning"
            className="p-0 [&>svg]:w-4 h-4"
          />
        </div>
      )}
      <div className="relative max-w-6xl mx-auto">
        {isBelowDesktop && (
          <header
            className={`lg:hidden bg-white p-4 py-3 sticky top-0 z-[10] ${
              !menuIsOpen && "drop-shadow-lg"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Old_Button
                  className="lg:hidden w-8 h-8"
                  variant="text"
                  onClick={handleClick}
                  aria-label="Toggle menu"
                >
                  {menuIsOpen ? <CrossIcon /> : <MenuIcon />}
                </Old_Button>
                {/* // TODO: WEB-1726 change to Link */}
                <a
                  className="shrink-0"
                  href={isAuthenticated ? "/home" : "/"}
                  aria-label="Golden Bride - International dating service"
                >
                  <Image
                    src={makeUrl(
                      CLOUDFRONT_FONTS_LINK,
                      "img/logo-text.38b79445.svg",
                    )}
                    alt="Golden Bride logo"
                    width={129}
                    height={43}
                    priority
                    loading="eager"
                  />
                </a>
              </div>
              <AuthRenderer>
                <AuthRenderer.IsUnauth>
                  <div className="flex justify-end gap-1 md:gap-2">
                    <Button
                      variant="primary"
                      onHoverVariant="color-lighten-green"
                      className="uppercase"
                      onClick={openSignInModal}
                    >
                      sign in
                    </Button>
                    {isFeatureFlagEnabled("NEW_LEFTBAR_DESIGN") && (
                      <Button
                        variant="default"
                        onHoverVariant="color-lighten-pink"
                        className="uppercase bg-gradient-red-pink-red hidden sm:block"
                        onClick={openRegisterModal}
                        aria-label="Sign up"
                      >
                        sign up
                      </Button>
                    )}
                  </div>
                </AuthRenderer.IsUnauth>
                <AuthRenderer.IsAuth>
                  <div>
                    <ButtonWithIcon
                      ariaLabel="Mail me"
                      onClick={handleMailboxButtonClick}
                      disabledHover
                      iconColor="green"
                      color="primary"
                      Icon={MailBoxIcon}
                      className={clsxm("!bg-white w-9 h-9 shadow mr-2")}
                    >
                      {parsed.success ? (
                        <span
                          className={`absolute left-[21px] -top-[4px] font-normal text-[10px] bg-pink text-white rounded-md ${
                            parsed.data.unreadMail > 9 ? "px-1" : "px-2"
                          }`}
                        >
                          {parsed.data.unreadMail > 100
                            ? "99+"
                            : parsed.data.unreadMail}
                        </span>
                      ) : null}
                    </ButtonWithIcon>

                    <ButtonWithIcon
                      ariaLabel="Chat with me"
                      onClick={handleChatButtonClick}
                      disabledHover
                      iconColor="white"
                      color="primary"
                      Icon={LiveChatIcon}
                      className={clsxm("!bg-gradient-green-blue-green w-9 h-9")}
                    />
                  </div>
                </AuthRenderer.IsAuth>
              </AuthRenderer>
            </div>
            <Drawer
              open={menuIsOpen}
              PaperProps={{
                sx: {
                  ...scrollBarSxStyles,
                  top: "70px",
                },
              }}
              onClose={handleClick}
              sx={{
                zIndex: 5,
                "& .MuiBackdrop-root": {
                  top: "70px",
                },
              }}
            >
              <Collapse in={menuIsOpen}>
                <Paper className="rounded-b-lg rounded-t-none pb-4">
                  <LeftMenu
                    ref={drawerLeftMenuRef}
                    isSidebarVersion
                    handleDrawerClose={handleClick}
                    profileData={data ?? null}
                  />
                </Paper>
              </Collapse>
            </Drawer>
          </header>
        )}
        <SecondaryLayoutTopBanner />
        <div className="flex">
          <CustomScroll>
            <aside className="bg-white lg:pr-[7px]">
              <LeftMenu
                ref={leftMenuRef}
                isMainBlockScrolledDown={isMainBlockScrolledDown}
                profileData={data ?? null}
              />
            </aside>
          </CustomScroll>
          <div
            className="px-0 py-4 md:p-4 md:pr-3 grow w-full min-w-0"
            ref={mainContentRef}
          >
            {beforeContent}
            <main className="bg-white">{children}</main>
            <AuthRenderer>
              <AuthRenderer.IsUnauth>
                <Footer />
              </AuthRenderer.IsUnauth>
            </AuthRenderer>
          </div>
        </div>
      </div>
    </div>
  );
};
